import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Plus, Trash, ArrowUp, ArrowDown, FloppyDisk, Check, X,
  Star, ArrowsClockwise, Warning,
} from '@phosphor-icons/react';

import { confirm } from '../../components/ConfirmDialog';
import { notify } from '../../lib/notify';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Dynamic pricing editor — super-admin only.
 *
 * Left column: plan cards (name, monthly price, popular flag, sort_order,
 * annual discount, tagline, cta). Add / delete / reorder tiers freely.
 *
 * Right column: features matrix — every row is a feature, every column is a
 * plan. Click the checkbox to include/exclude a feature in a plan. Add /
 * delete / reorder features. All edits persist immediately to Mongo.
 *
 * The public `/pricing` page reads from the same collections and re-renders
 * whichever tiers exist — no code change needed to add a 5th tier.
 */
export default function PricingEditor() {
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [newFeatureName, setNewFeatureName] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [pRes, fRes] = await Promise.all([
        axios.get(`${API}/admin/plans`),
        axios.get(`${API}/admin/features`),
      ]);
      setPlans((pRes.data.plans || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      setFeatures((fRes.data.features || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patchPlan = async (id, updates) => {
    setSavingId(`plan-${id}`);
    try {
      const r = await axios.put(`${API}/admin/plans/${id}`, updates);
      setPlans(prev => prev.map(p => p.id === id ? r.data : p).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to save plan');
    } finally {
      setSavingId(null);
    }
  };

  const patchFeature = async (id, updates) => {
    setSavingId(`feat-${id}`);
    try {
      const r = await axios.put(`${API}/admin/features/${id}`, updates);
      setFeatures(prev => prev.map(f => f.id === id ? r.data : f).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to save feature');
    } finally {
      setSavingId(null);
    }
  };

  const toggleFeaturePlan = (feature, planId) => {
    const has = (feature.plan_ids || []).includes(planId);
    const next = has ? feature.plan_ids.filter(x => x !== planId) : [...(feature.plan_ids || []), planId];
    patchFeature(feature.id, { plan_ids: next });
  };

  const addFeature = async () => {
    const name = newFeatureName.trim();
    if (!name) return;
    try {
      const r = await axios.post(`${API}/admin/features`, {
        name,
        plan_ids: [],
        sort_order: (features[features.length - 1]?.sort_order || 0) + 1,
      });
      setFeatures(prev => [...prev, r.data]);
      setNewFeatureName('');
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to add feature');
    }
  };

  const deleteFeature = async (id) => {
    if (!await confirm({ message: 'Delete this feature? This removes it from the public /pricing page immediately.' })) return;
    try {
      await axios.delete(`${API}/admin/features/${id}`);
      setFeatures(prev => prev.filter(f => f.id !== id));
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const moveFeature = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= features.length) return;
    const a = features[idx], b = features[j];
    // Swap sort_order values, persist both, then re-sort locally.
    patchFeature(a.id, { sort_order: b.sort_order });
    patchFeature(b.id, { sort_order: a.sort_order });
  };

  const movePlan = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= plans.length) return;
    const a = plans[idx], b = plans[j];
    patchPlan(a.id, { sort_order: b.sort_order });
    patchPlan(b.id, { sort_order: a.sort_order });
  };

  const deletePlan = async (id) => {
    if (!await confirm({ message: `Delete plan "${id}"? Every feature will be un-assigned from it. This affects the public /pricing page immediately.` })) return;
    try {
      await axios.delete(`${API}/admin/plans/${id}`);
      setPlans(prev => prev.filter(p => p.id !== id));
      // Refetch features because backend cascaded plan_ids removal
      const fRes = await axios.get(`${API}/admin/features`);
      setFeatures((fRes.data.features || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const featureCountByPlan = useMemo(() => {
    const m = {};
    for (const p of plans) m[p.id] = 0;
    for (const f of features) for (const pid of (f.plan_ids || [])) if (m[pid] != null) m[pid]++;
    return m;
  }, [plans, features]);

  if (loading) return <div className="p-6 text-[#00a69c]">Loading pricing…</div>;

  return (
    <div className="space-y-6" data-testid="pricing-editor">
      {error && (
        <div className="medical-card p-4 border-l-4 border-red-500 text-sm text-red-700 flex items-center gap-2">
          <Warning size={16} /> {error}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#6D8E87]">
          Edits publish instantly to the public <code className="px-1.5 py-0.5 rounded bg-[#F4F9F7] text-[#00a69c]">/pricing</code> page.
        </p>
        <div className="flex gap-2">
          <button onClick={load} className="px-3 py-1.5 text-xs font-medium text-[#3B5A54] border border-[#D5E8E4] rounded-lg hover:bg-[#F4F9F7] inline-flex items-center gap-1.5" data-testid="pricing-reload">
            <ArrowsClockwise size={14} /> Reload
          </button>
          <button onClick={() => setShowAddPlan(true)} className="btn-primary flex items-center gap-2" data-testid="add-plan-button">
            <Plus size={16} weight="bold" /> Add plan
          </button>
        </div>
      </div>

      {/* Plans row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plans-grid">
        {plans.map((p, idx) => (
          <PlanCard
            key={p.id}
            plan={p}
            featureCount={featureCountByPlan[p.id] || 0}
            saving={savingId === `plan-${p.id}`}
            onSave={(updates) => patchPlan(p.id, updates)}
            onDelete={() => deletePlan(p.id)}
            onMoveUp={idx > 0 ? () => movePlan(idx, -1) : null}
            onMoveDown={idx < plans.length - 1 ? () => movePlan(idx, +1) : null}
          />
        ))}
        {plans.length === 0 && (
          <div className="col-span-full medical-card p-8 text-center text-sm text-[#6D8E87]">
            No plans yet. Click <b>Add plan</b> to create one.
          </div>
        )}
      </div>

      {/* Features matrix */}
      <div className="medical-card">
        <div className="p-4 border-b border-[#D5E8E4] flex flex-wrap items-center gap-3 justify-between">
          <h3 className="text-base font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
            Features · {features.length}
          </h3>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={newFeatureName}
              onChange={(e) => setNewFeatureName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addFeature(); }}
              placeholder="New feature name…"
              className="px-3 py-1.5 border border-[#D5E8E4] rounded-lg text-sm w-64"
              data-testid="new-feature-input"
            />
            <button onClick={addFeature} disabled={!newFeatureName.trim()}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-[#00a69c] hover:bg-[#008b83] rounded-lg disabled:bg-[#00a69c]/40 inline-flex items-center gap-1"
              data-testid="add-feature-button">
              <Plus size={14} weight="bold" /> Add
            </button>
          </div>
        </div>

        {plans.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6D8E87]">Add a plan first — features need at least one tier to attach to.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F9F7]">
                <tr>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#3B5A54] w-8">#</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#3B5A54]">Feature</th>
                  {plans.map(p => (
                    <th key={p.id} className="px-2 py-3 text-center text-xs uppercase tracking-wider text-[#3B5A54] min-w-[110px]">
                      {p.name}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-[#3B5A54] w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {features.map((f, idx) => (
                  <FeatureRow
                    key={f.id}
                    feature={f}
                    idx={idx}
                    plans={plans}
                    saving={savingId === `feat-${f.id}`}
                    onToggle={(pid) => toggleFeaturePlan(f, pid)}
                    onRename={(name) => patchFeature(f.id, { name })}
                    onDelete={() => deleteFeature(f.id)}
                    onMoveUp={idx > 0 ? () => moveFeature(idx, -1) : null}
                    onMoveDown={idx < features.length - 1 ? () => moveFeature(idx, +1) : null}
                  />
                ))}
                {features.length === 0 && (
                  <tr><td colSpan={plans.length + 3} className="px-4 py-10 text-center text-[#6D8E87]">No features yet. Add one above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddPlan && (
        <AddPlanModal
          existingPlanIds={plans.map(p => p.id)}
          onClose={() => setShowAddPlan(false)}
          onCreated={(p) => { setPlans(prev => [...prev, p].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))); setShowAddPlan(false); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────── plan card ─────────────────────────── */
function PlanCard({ plan, featureCount, saving, onSave, onDelete, onMoveUp, onMoveDown }) {
  const [draft, setDraft] = useState(plan);
  useEffect(() => { setDraft(plan); }, [plan]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(plan);
  const submit = () => {
    onSave({
      name: draft.name, tagline: draft.tagline, cta: draft.cta,
      monthly_price: draft.monthly_price, annual_discount_pct: draft.annual_discount_pct,
      popular: draft.popular,
    });
  };

  return (
    <div className={`medical-card p-4 space-y-2 relative ${draft.popular ? 'border-2 border-[#00a69c]' : ''}`} data-testid={`plan-card-${plan.id}`}>
      {draft.popular && (
        <div className="absolute -top-2 left-3 px-2 py-0.5 bg-[#00a69c] text-white text-[10px] rounded-full font-bold uppercase tracking-wider">Popular</div>
      )}
      <div className="flex items-center justify-between gap-2">
        <code className="text-[10px] uppercase tracking-widest text-[#6D8E87]">{plan.id}</code>
        <div className="flex gap-1">
          {onMoveUp && <IconBtn title="Move up" onClick={onMoveUp} data-testid={`plan-up-${plan.id}`}><ArrowUp size={12} /></IconBtn>}
          {onMoveDown && <IconBtn title="Move down" onClick={onMoveDown} data-testid={`plan-down-${plan.id}`}><ArrowDown size={12} /></IconBtn>}
          <IconBtn title="Delete plan" danger onClick={onDelete} data-testid={`plan-delete-${plan.id}`}><Trash size={12} /></IconBtn>
        </div>
      </div>
      <input
        type="text" value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        className="w-full text-lg font-bold px-2 py-1 border border-[#D5E8E4] rounded"
        data-testid={`plan-name-${plan.id}`}
      />
      <textarea
        value={draft.tagline || ''} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })}
        placeholder="Tagline…" rows={2}
        className="w-full text-xs px-2 py-1 border border-[#D5E8E4] rounded resize-none"
        data-testid={`plan-tagline-${plan.id}`}
      />
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs">
          <span className="block text-[#6D8E87] mb-0.5">$/user/mo</span>
          <input type="number" value={draft.monthly_price ?? 0}
            onChange={(e) => setDraft({ ...draft, monthly_price: Number(e.target.value) })}
            className="w-full px-2 py-1 border border-[#D5E8E4] rounded"
            data-testid={`plan-price-${plan.id}`} />
        </label>
        <label className="text-xs">
          <span className="block text-[#6D8E87] mb-0.5">Annual % off</span>
          <input type="number" value={draft.annual_discount_pct ?? 0}
            onChange={(e) => setDraft({ ...draft, annual_discount_pct: Number(e.target.value) })}
            className="w-full px-2 py-1 border border-[#D5E8E4] rounded"
            data-testid={`plan-annual-${plan.id}`} />
        </label>
      </div>
      <input type="text" value={draft.cta || ''}
        onChange={(e) => setDraft({ ...draft, cta: e.target.value })}
        placeholder="CTA button text"
        className="w-full text-xs px-2 py-1 border border-[#D5E8E4] rounded"
        data-testid={`plan-cta-${plan.id}`} />

      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <input type="checkbox" checked={!!draft.popular}
          onChange={(e) => setDraft({ ...draft, popular: e.target.checked })}
          className="accent-[#00a69c]"
          data-testid={`plan-popular-${plan.id}`} />
        <Star size={12} weight={draft.popular ? 'fill' : 'regular'} className="text-[#00a69c]" />
        Mark as most popular
      </label>

      <div className="pt-2 border-t border-[#D5E8E4] flex items-center justify-between">
        <span className="text-[11px] text-[#6D8E87]">{featureCount} feature{featureCount === 1 ? '' : 's'}</span>
        <button onClick={submit} disabled={!dirty || saving}
          className="px-2.5 py-1 text-xs font-semibold text-white bg-[#00a69c] hover:bg-[#008b83] rounded disabled:bg-[#D5E8E4] disabled:text-[#6D8E87] inline-flex items-center gap-1"
          data-testid={`plan-save-${plan.id}`}>
          {saving ? '…' : <><FloppyDisk size={12} /> Save</>}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── feature row ─────────────────────────── */
function FeatureRow({ feature, idx, plans, saving, onToggle, onRename, onDelete, onMoveUp, onMoveDown }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(feature.name);
  useEffect(() => { setDraft(feature.name); }, [feature.name]);

  const commitRename = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== feature.name) onRename(draft.trim());
    else setDraft(feature.name);
  };

  return (
    <tr className="border-t border-[#D5E8E4] hover:bg-[#F4F9F7]" data-testid={`feature-row-${feature.id}`}>
      <td className="px-4 py-2 text-xs text-[#6D8E87]">{idx + 1}</td>
      <td className="px-4 py-2">
        {editing ? (
          <input
            autoFocus type="text" value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setDraft(feature.name); setEditing(false); } }}
            className="w-full px-2 py-1 border border-[#00a69c] rounded text-sm"
            data-testid={`feature-name-input-${feature.id}`}
          />
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="text-left text-sm text-[#052A23] hover:text-[#00a69c] w-full truncate"
            data-testid={`feature-name-${feature.id}`}
            title="Click to rename"
          >
            {feature.name}
          </button>
        )}
      </td>
      {plans.map(p => {
        const on = (feature.plan_ids || []).includes(p.id);
        return (
          <td key={p.id} className="px-2 py-2 text-center">
            <button
              onClick={() => onToggle(p.id)}
              disabled={saving}
              className={`w-7 h-7 rounded-md inline-flex items-center justify-center transition ${
                on ? 'bg-[#00a69c] text-white hover:bg-[#008b83]' : 'bg-white border border-[#D5E8E4] text-[#D5E8E4] hover:border-[#00a69c] hover:text-[#00a69c]'
              }`}
              data-testid={`feature-toggle-${feature.id}-${p.id}`}
              aria-pressed={on}
            >
              {on ? <Check size={14} weight="bold" /> : <X size={14} />}
            </button>
          </td>
        );
      })}
      <td className="px-4 py-2 text-center">
        <div className="inline-flex gap-1">
          {onMoveUp && <IconBtn title="Move up" onClick={onMoveUp} data-testid={`feature-up-${feature.id}`}><ArrowUp size={12} /></IconBtn>}
          {onMoveDown && <IconBtn title="Move down" onClick={onMoveDown} data-testid={`feature-down-${feature.id}`}><ArrowDown size={12} /></IconBtn>}
          <IconBtn title="Delete" danger onClick={onDelete} data-testid={`feature-delete-${feature.id}`}><Trash size={12} /></IconBtn>
        </div>
      </td>
    </tr>
  );
}

function IconBtn({ children, danger, ...rest }) {
  return (
    <button
      {...rest}
      className={`w-6 h-6 rounded inline-flex items-center justify-center border transition ${
        danger
          ? 'border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300'
          : 'border-[#D5E8E4] text-[#3B5A54] hover:bg-[#F4F9F7] hover:border-[#00a69c] hover:text-[#00a69c]'
      }`}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────── add plan modal ─────────────────────────── */
function AddPlanModal({ existingPlanIds, onClose, onCreated }) {
  const [form, setForm] = useState({
    id: '', name: '', monthly_price: 0, annual_discount_pct: 20,
    tagline: '', cta: '', popular: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const idClash = form.id && existingPlanIds.includes(form.id.trim().toLowerCase());

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      const r = await axios.post(`${API}/admin/plans`, {
        ...form,
        name: form.name.trim(),
        id: form.id.trim().toLowerCase() || undefined,
      });
      onCreated(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="add-plan-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3">
        <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Add pricing tier</h3>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">
            <span className="block font-semibold text-[#3B5A54] mb-1">Name *</span>
            <input required type="text" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded text-sm"
              placeholder="Business Pro"
              data-testid="new-plan-name" />
          </label>
          <label className="text-xs">
            <span className="block font-semibold text-[#3B5A54] mb-1">ID (kebab-case)</span>
            <input type="text" value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
              className={`w-full px-3 py-2 border rounded text-sm font-mono ${idClash ? 'border-red-400' : 'border-[#D5E8E4]'}`}
              placeholder="auto-generated"
              data-testid="new-plan-id" />
          </label>
        </div>
        {idClash && <div className="text-xs text-red-600">A plan with this id already exists.</div>}
        <label className="block text-xs">
          <span className="block font-semibold text-[#3B5A54] mb-1">Tagline</span>
          <input type="text" value={form.tagline}
            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
            className="w-full px-3 py-2 border border-[#D5E8E4] rounded text-sm"
            placeholder="For teams that…"
            data-testid="new-plan-tagline" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">
            <span className="block font-semibold text-[#3B5A54] mb-1">Monthly $ / user</span>
            <input type="number" value={form.monthly_price}
              onChange={(e) => setForm({ ...form, monthly_price: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded text-sm"
              data-testid="new-plan-price" />
          </label>
          <label className="text-xs">
            <span className="block font-semibold text-[#3B5A54] mb-1">Annual % off</span>
            <input type="number" value={form.annual_discount_pct}
              onChange={(e) => setForm({ ...form, annual_discount_pct: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-[#D5E8E4] rounded text-sm"
              data-testid="new-plan-annual" />
          </label>
        </div>
        <label className="block text-xs">
          <span className="block font-semibold text-[#3B5A54] mb-1">CTA text</span>
          <input type="text" value={form.cta}
            onChange={(e) => setForm({ ...form, cta: e.target.value })}
            className="w-full px-3 py-2 border border-[#D5E8E4] rounded text-sm"
            placeholder={form.name ? `Start with ${form.name}` : 'Get started'}
            data-testid="new-plan-cta" />
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={form.popular}
            onChange={(e) => setForm({ ...form, popular: e.target.checked })}
            className="accent-[#00a69c]"
            data-testid="new-plan-popular" />
          Mark as most popular
        </label>
        {err && <div className="text-xs text-red-600" data-testid="new-plan-error">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={saving}
            className="px-3 py-2 text-sm text-[#3B5A54] border border-[#D5E8E4] rounded hover:bg-[#F4F9F7]"
            data-testid="new-plan-cancel">Cancel</button>
          <button type="submit" disabled={!form.name.trim() || idClash || saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-[#00a69c] hover:bg-[#008b83] rounded disabled:bg-[#00a69c]/40"
            data-testid="new-plan-submit">
            {saving ? 'Creating…' : 'Create plan'}
          </button>
        </div>
      </form>
    </div>
  );
}
